import autosize from "autosize";
import { registerFeatureBootstrap } from "./bootstrap";

export type AutosizeTarget = ParentNode | HTMLTextAreaElement | HTMLTextAreaElement[];

export type WMUAutosizeAPI = {
  init: (target?: AutosizeTarget) => void;
  update: (target?: AutosizeTarget) => void;
  destroy: (target?: AutosizeTarget) => void;
};

type AutosizeWindow = Window & {
  WMUAutosize?: WMUAutosizeAPI;
  __WMU_AUTOSIZE_BOOTED?: boolean;
};

const SELECTOR = 'textarea[data-autosize]:not([data-autosize="false"]), textarea.form-textarea:not([data-autosize="false"])';

function toTextareas(target: AutosizeTarget = document): HTMLTextAreaElement[] {
  if (target instanceof HTMLTextAreaElement) {
    return [target];
  }
  if (Array.isArray(target)) {
    return target.filter((node): node is HTMLTextAreaElement => node instanceof HTMLTextAreaElement);
  }
  return Array.from(target.querySelectorAll<HTMLTextAreaElement>(SELECTOR));
}

function init(target: AutosizeTarget = document): void {
  const textareas = toTextareas(target);
  if (textareas.length === 0) {
    return;
  }
  autosize(textareas);
  textareas.forEach((textarea) => {
    textarea.dataset.autosizeReady = "true";
  });
}

function update(target: AutosizeTarget = document): void {
  const textareas = toTextareas(target);
  if (textareas.length === 0) {
    return;
  }
  const pending = textareas.filter((textarea) => textarea.dataset.autosizeReady !== "true");
  if (pending.length > 0) {
    init(pending);
  }
  autosize.update(textareas);
}

function destroy(target: AutosizeTarget = document): void {
  const textareas = toTextareas(target);
  if (textareas.length === 0) {
    return;
  }
  autosize.destroy(textareas);
  textareas.forEach((textarea) => {
    delete textarea.dataset.autosizeReady;
    textarea.style.height = "";
    textarea.style.resize = "";
    textarea.style.overflowY = "";
  });
}

const autosizeEventCleanup = new WeakMap<HTMLElement, () => void>();

function mountAutosizeEvents(root: HTMLElement): void {
  const doc = root.ownerDocument;
  const onTextUpdated = (event: Event): void => {
    if (event instanceof CustomEvent && event.detail instanceof HTMLTextAreaElement) {
      update(event.detail);
      return;
    }
    update(doc);
  };
  doc.addEventListener("wmu:text-updated", onTextUpdated);
  autosizeEventCleanup.set(root, () => {
    doc.removeEventListener("wmu:text-updated", onTextUpdated);
  });
}

function unmountAutosizeEvents(root: HTMLElement): void {
  autosizeEventCleanup.get(root)?.();
  autosizeEventCleanup.delete(root);
}

const autosizeWindow = window as AutosizeWindow;

if (!autosizeWindow.__WMU_AUTOSIZE_BOOTED) {
  autosizeWindow.__WMU_AUTOSIZE_BOOTED = true;
  autosizeWindow.WMUAutosize = { init, update, destroy };
  registerFeatureBootstrap({
    key: "autosize:textareas",
    selector: SELECTOR,
    mount(root) {
      update(root);
    },
    unmount(root) {
      destroy(root);
    },
  });
  registerFeatureBootstrap({
    key: "autosize:events",
    selector: "body",
    mount: mountAutosizeEvents,
    unmount: unmountAutosizeEvents,
  });
}
